/* eslint-disable @typescript-eslint/no-unused-vars */
export const jwtUtils = {
  /**
   * Decodifica el payload de un JWT token de forma segura
   * @param {string} token - El JWT token
   * @returns {Object|null} - El payload decodificado o null si hay error
   */
  decodeToken(token: string) {
    if (!token || typeof token !== 'string') return null

    try {
      const parts = token.split('.')
      if (parts.length !== 3) return null

      // Decodificar el payload (segunda parte)
      const payload = parts[1]

      // Agregar padding si es necesario
      const paddedPayload = payload + '='.repeat((4 - (payload.length % 4)) % 4)

      const decodedPayload = atob(paddedPayload)
      return JSON.parse(decodedPayload)
    } catch (error) {
      console.error('Error decodificando JWT:', error)
      return null
    }
  },

  /**
   * Verifica si un token está expirado
   * @param {string} token - El JWT token
   * @returns {boolean} - true si está expirado
   */
  isTokenExpired(token: string) {
    const payload = this.decodeToken(token)
    if (!payload || !payload.exp) return true

    const currentTime = Math.floor(Date.now() / 1000) // JWT usa segundos
    return payload.exp < currentTime
  },

  /**
   * Verifica si el token expirará pronto
   * @param {string} token - El JWT token
   * @param {number} thresholdMinutes - Minutos antes de la expiración
   * @returns {boolean} - true si expira pronto
   */
  isTokenExpiringSoon(token: string, thresholdMinutes = 5) {
    const payload = this.decodeToken(token)
    if (!payload || !payload.exp) return true

    const currentTime = Math.floor(Date.now() / 1000)
    const thresholdSeconds = thresholdMinutes * 60

    return payload.exp - currentTime <= thresholdSeconds
  },

  /**
   * Obtiene el tiempo restante hasta la expiración
   * @param {string} token - El JWT token
   * @returns {number} - Segundos hasta la expiración, 0 si ya expiró
   */
  getTimeUntilExpiration(token: string) {
    const payload = this.decodeToken(token)
    if (!payload || !payload.exp) return 0

    const currentTime = Math.floor(Date.now() / 1000)
    const timeUntilExpiration = payload.exp - currentTime

    return Math.max(0, timeUntilExpiration)
  },

  /**
   * Extrae información del usuario del token JWT
   * @param {string} token - El JWT token
   * @returns {Object|null} - Información del usuario
   */
  getUserFromToken(token: string) {
    const payload = this.decodeToken(token)
    if (!payload) return null

    return {
      id: payload.sub || payload.userId || payload.id,
      email: payload.email,
      name: payload.name || payload.username,
      role: payload.role || payload.roles,
      permissions: payload.permissions || [],
      exp: payload.exp,
      iat: payload.iat,
    }
  },

  /**
   * Valida la estructura básica del JWT
   * @param {string} token - El JWT token
   * @returns {boolean} - true si tiene estructura válida
   */
  isValidJWTStructure(token: string) {
    if (!token || typeof token !== 'string') return false

    const parts = token.split('.')
    if (parts.length !== 3) return false

    // Verificar que cada parte sea base64url válida
    try {
      parts.forEach((part) => {
        if (part.length === 0) throw new Error('Empty part')
        atob(part + '='.repeat((4 - (part.length % 4)) % 4))
      })
      return true
    } catch (error: unknown) {
      return false
    }
  },
}
